// src/pages/RaiseTicketPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchTickets,
  createTicket,
  updateTicket,
  deleteTicket,
  clearError,
  setInProgress,
  closeTicket,
  reopenTicket,
  resolveTicket,
  acknowledgeTicket,
  fetchMessages,
  sendMessage,
  editMessage,
  removeMessage,
  fetchITSubcategories,
  fetchNonITSubcategories,
  fetchCategories, // NEW IMPORT
  setPendingAcknowledgement,
  clearPendingAcknowledgement,
  setRealtimeTickets,
} from "store/slices/ticketSlice";
import { fetchTicketLogs } from "store/slices/ticketLogSlice";
import {
  fetchTicketEmployeesIT,
  fetchTicketEmployeesNonIT,
  restoreRole,
} from "store/slices/commonSlice";
import { fetchEmployees } from "store/slices/employeeSlice";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import { format, isValid, parseISO } from "date-fns";
import {
  Pencil,
  Trash2,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MoreHorizontal,
  Send,
  Edit2,
  Trash,
  MoreVertical,
  History,
  Loader2,
  Eye,
} from "lucide-react";
import DataTable from "components/common/DataTable";
import PageHeader from "components/common/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Avatar, AvatarFallback } from "components/ui/avatar";
import { ScrollArea } from "components/ui/scroll-area";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import useIssuanceSocket from "hooks/useIssuanceSocket";
import employeeApi from "api/employeeApi";
import { setEmployeesForTicket } from "store/slices/employeeSlice";
import profileApi from "api/profileApi";
import { cn } from "lib/utils";
import FormField from "components/common/FormField";
import {
  fetchActivityLogs,
  clearError as clearActivityLogError,
} from "store/slices/activityLogSlice";
import LogDataDisplay from "components/common/LogDataDisplay";
import { canManageEverything } from "utlis/authUtils";
import { formatDateTime } from "utlis/formateDateTime";

const priorityColors = {
  high: "destructive",
  medium: "default",
  low: "secondary",
};

const statusIcons = {
  open: <AlertCircle className="h-4 w-4" />,
  in_progress: <Clock className="h-4 w-4" />,
  closed: <CheckCircle className="h-4 w-4" />,
  resolved: <CheckCircle className="h-4 w-4 text-green-500" />,
  reopened: <AlertCircle className="h-4 w-4 text-orange-500" />,
};

const RaiseTicketPage = () => {
  useIssuanceSocket();
  usePermissionSocket();
  const dispatch = useDispatch();

  const isSuperAdmin = canManageEverything();

  const {
    tickets,
    messages,
    pagination,
    isLoading,
    error,
    itSubcategories,
    nonItSubcategories,
    categories, // NEW STATE
    subcategoriesLoading,
    categoriesLoading, // NEW STATE
  } = useSelector((s) => s.ticket);

  const { ticketEmployeesIT, ticketEmployeesNonIT } = useSelector(
    (state) => state.common
  );

  const { employees = [] } = useSelector((s) => s.employee);
  const {
    logs: ticketLogs,
    isLoading: logsLoading,
    error: logsError,
  } = useSelector((s) => s.activityLog);

  const { canCreate, canUpdate, canDelete, canRead } = usePermissions("ticket");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);

  const [isITRelated, setIsITRelated] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false); // <-- NEW: Controls history dialog
  const [selectedTicketId, setSelectedTicketId] = useState(null); // <-- NEW: Stores current ticket ID for history

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "", // NEW FIELD
    sub_category: "",
    priority: "medium",
    assigned_to: "",
    expected_solution_end_datetime: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [newMessage, setNewMessage] = useState("");
  const [editingMsg, setEditingMsg] = useState(null);
  const [ackNotes, setAckNotes] = useState("");
  const [canApproveOrReject, setCanApproveOrReject] = useState(false);

  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveRemark, setResolveRemark] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { pendingAcknowledgement } = useSelector((s) => s.ticket);

  // 1. Fetch Categories on mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // 2. Fetch Employees based on Toggle
  useEffect(() => {
    if (isITRelated) {
      dispatch(fetchTicketEmployeesIT());
    } else {
      dispatch(fetchTicketEmployeesNonIT());
    }
  }, [dispatch, isITRelated]);

  // 3. Fetch Subcategories when Category changes
  // We pass the category ID to the API action
  useEffect(() => {
    // Only fetch if we have a category selected
    if (formData.category) {
      if (isITRelated) {
        dispatch(fetchITSubcategories(formData.category));
      } else {
        dispatch(fetchNonITSubcategories(formData.category));
      }
    } else {
      // Optional: Clear subcategories if category is cleared
      // dispatch(clearSubcategories()); // if you had such an action
    }
  }, [formData.category, isITRelated, dispatch]);

  // Load profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await profileApi.getMyProfile();
        setCanApproveOrReject(
          profile?.can_manage_issuance_it || profile?.can_manage_issuance_non_it
        );
      } catch (err) {
        console.error("Failed to load profile for issuance permissions", err);
      }
    };
    loadProfile();
  }, []);

  // check created user
  const user = JSON.parse(localStorage.getItem("employee"));

  const loggedInId = user?.id;

  const msgs = messages[currentTicket?.id] || [];
  const firstMessage = msgs[0];

  const isAllowed =
    firstMessage &&
    firstMessage?.created_by &&
    firstMessage?.created_by === loggedInId;

  const refetch = useCallback(() => {
    dispatch(fetchTickets({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Legacy effect: keeping it to be safe, though logic moved to specific useEffect above
  useEffect(() => {
    // We rely on the specific useEffect for formData.category now, 
    // but we keep this to handle initial loads if necessary.
    // dispatch(fetchITSubcategories());
    // dispatch(fetchNonITSubcategories());
  }, [dispatch]);

  useEffect(() => {
    employeeApi
      .getForTicketAssignment(isITRelated)
      .then((res) => {
        dispatch(setEmployeesForTicket(res)); // ← Perfect! Uses Redux properly
      })
      .catch((err) => {
        console.error("Failed to load ticket handlers:", err);
        toast.error("Failed to load employees for assignment");
      });
  }, [dispatch, isITRelated]);

  const openForm = (ticket = null) => {
  setCurrentTicket(ticket);
  
  let isTicketIT = true; // default for new tickets
  if (ticket && ticket.category_is_it !== undefined) {
    isTicketIT = ticket.category_is_it === true;
  }
  setIsITRelated(isTicketIT);

  setFormData(
    ticket
      ? {
        title: ticket?.title || "",
        description: ticket?.description || "",
        category: ticket?.category_id || "",
        sub_category: ticket?.sub_category || "",
        priority: ticket?.priority || "medium",
        assigned_to: ticket?.assigned_to || "",
        expected_solution_end_datetime:
          ticket?.expected_solution_end_datetime?.slice(0, 16) || "",
      }
      : {
        title: "",
        description: "",
        category: "", 
        sub_category: "", 
        priority: "medium",
        assigned_to: "",
        expected_solution_end_datetime: "",
      }
  );
  setFormErrors({});
  setDialogOpen(true);
  
  // Fetch subcategories immediately when editing existing ticket
  if(ticket?.category_id) {
    if(isTicketIT) {
      dispatch(fetchITSubcategories(ticket.category_id));
    } else {
      dispatch(fetchNonITSubcategories(ticket.category_id));
    }
  }
};

  const openHistory = async (ticket) => {
    setSelectedTicketId(ticket.id);
    setHistoryOpen(true);

    dispatch(
      fetchActivityLogs({
        page: 1,
        page_size: 5000,
        resource: "ticket",
        object_id: ticket.id,
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData?.title.trim()) errors.title = "Title is required";
    if (!formData?.description.trim())
      errors.description = "Description is required";
    if (!formData?.sub_category)
      errors.sub_category = "Subcategory is required";
    if (!formData?.assigned_to)
      errors.assigned_to = "Please assign to an employee";
    // if (!formData?.expected_solution_end_datetime)
    //   errors.expected_solution_end_datetime = "Expected resolution time is required";

    if (Object.keys(errors)?.length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const payload = {
        ...formData,
        expected_solution_end_datetime: null,
      };

      if (currentTicket) {
        await dispatch(
          updateTicket({ id: currentTicket.id, ...payload })
        ).unwrap();
        console.log("payload", payload);
        toast.success("Ticket updated successfully");
      } else {
        await dispatch(createTicket(payload)).unwrap();
        toast.success("Ticket raised successfully");
      }
      setDialogOpen(false);
      refetch();
    } catch (err) {
      console.log("err", err);
      const apiErrors = {};
      if (err && typeof err === "object") {
        Object.keys(err).forEach((key) => {
          apiErrors[key] = Array.isArray(err[key]) ? err[key][0] : err[key];
        });
      }
      setFormErrors(apiErrors);
      toast.error("Failed to save ticket");
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("No permission");
    await dispatch(deleteTicket(currentTicket.id)).unwrap();
    toast.success("Ticket deleted");
    setDeleteOpen(false);
    refetch();
  };

  const openDetail = async (ticket) => {
    setCurrentTicket(ticket);
    await dispatch(fetchMessages(ticket.id));
    setDetailOpen(true);
  };

  const sendMsg = async () => {
    if (!newMessage.trim()) return;
    await dispatch(
      sendMessage({ ticketId: currentTicket.id, message: newMessage })
    ).unwrap();
    setNewMessage("");
  };

  // Filter Categories based on Toggle
  const filteredCategories = useMemo(() => {
    return categories.filter(cat => cat.is_it === isITRelated);
  }, [categories, isITRelated]);

  const currentSubcategories = isITRelated
    ? itSubcategories
    : nonItSubcategories;

  const currentEmployees = isITRelated
    ? ticketEmployeesIT.items
    : ticketEmployeesNonIT.items;
  const employeesLoading = isITRelated
    ? ticketEmployeesIT.loading
    : ticketEmployeesNonIT.loading;

  // Get the latest message with old_ticket_status (most recent first)
  // const latestStatusChange = useMemo(() => {
  //   const relevantMessages = (messages[currentTicket?.id] || [])
  //     .filter(msg => msg.old_ticket_status && msg.old_ticket_status.length > 0)
  //     .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // newest first

  //   if (relevantMessages.length === 0) return null;

  //   const change = relevantMessages[0].old_ticket_status[0];
  //   return {
  //     oldStatus: change.status?.old?.toLowerCase(),
  //     newStatus: change.status?.new?.toLowerCase(),
  //   };
  // }, [messages, currentTicket?.id]);

  // Extract ALL distinct old statuses from message history
  const previousStatuses = useMemo(() => {
    const oldStatuses = new Set();

    const msgs = messages[currentTicket?.id] || [];

    msgs.forEach((msg) => {
      if (msg.old_ticket_status && Array.isArray(msg.old_ticket_status)) {
        msg.old_ticket_status.forEach((change) => {
          if (change.status?.old) {
            oldStatuses.add(change.status.old.toLowerCase().trim());
          }
        });
      }
    });

    return Array.from(oldStatuses);
  }, [messages, currentTicket?.id]);

  // Check if the ticket is currently escalated (latest "new" status is "Escalated")
  const isCurrentlyEscalated = useMemo(() => {
    const msgs = messages[currentTicket?.id] || [];

    // Find the most recent message with status change
    const latestChangeMsg = msgs
      .filter(
        (msg) => msg.old_ticket_status && msg.old_ticket_status.length > 0
      )
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

    if (!latestChangeMsg) return false;

    const latestChange = latestChangeMsg.old_ticket_status[0];
    return latestChange?.status?.new?.toLowerCase().trim() === "escalated";
  }, [messages, currentTicket?.id]);

  // For convenience: Check if "in_progress" was ever a previous status
  const wasInProgress = previousStatuses.includes("in progress");
  const wasOpen = previousStatuses.includes("open");
  // const wasResolved = previousStatuses.includes('resolved');
  const wasInAnyProgressState = previousStatuses.length > 0; // any transition happened

  const columns = [
    {
      accessorKey: "ticket_code",
      header: "Code",
      cell: ({ row }) => row.original?.ticket_code ?? "—",
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => row.original?.title ?? "-",
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: ({ row }) => (
        <Badge variant={priorityColors[row?.original?.priority]}>
          {row?.original?.priority.toUpperCase()}
        </Badge>
      ),
    },
    {
      accessorKey: "status_display",
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={
            row?.original?.status_display === "open"
              ? "default"
              : row?.original?.status_display === "closed"
                ? "secondary"
                : "outline"
          }
          className="gap-1.5"
        >
          {statusIcons[row?.original?.status_display] || (
            <AlertCircle className="h-4 w-4" />
          )}{" "}
          {row?.original?.status_display.replace("_", " ")}
        </Badge>
      ),
    },
     {
      accessorKey: "category_name",
      header: "category",
      cell: ({ row }) => (row.original?.category_name?? "-"),
    },
      {
      accessorKey: "sub_category_name",
      header: "Sub Category",
      cell: ({ row }) => (row.original?.sub_category_name?? "-"),
    },
    {
      accessorKey: "created_at",
      header: "Raised",
      cell: ({ row }) => (formatDateTime(row.original?.created_at) ?? "-"),
    },
    {
      accessorKey: "created_by_name",
      header: "Created By",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.created_by_name ?? "-"}</span>
      ),
    },
    {
      id: "actions",
      /*************  ✨ Windsurf Command ⭐  *************/
      /**
       * Cell component for the actions column of the Raise Ticket table.
       *
       * @param {object} row - The current row of the table.
       * @returns {JSX.Element} - A dropdown menu containing view, edit, and delete options.
       */
      /*******  0038f72b-9689-4afc-b006-5dab0e9f3fce  *******/ header:
        "Actions",

      cell: ({ row }) => {
        const t = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(canUpdate || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => openDetail(t)}>
                  <MessageSquare className="mr-2 h-4 w-4" /> View
                </DropdownMenuItem>
              )}
              {(canUpdate || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => openHistory(t)}>
                  <History className="mr-2 h-4 w-4" /> Ticket History
                </DropdownMenuItem>
              )}
              {(canUpdate || isSuperAdmin) && (
                <DropdownMenuItem onClick={() => openForm(t)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {(canDelete || isSuperAdmin) && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentTicket(t);
                    setDeleteOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Get the latest version of the open ticket from Redux
  const realtimeCurrentTicket = useSelector((state) =>
    currentTicket?.id
      ? state.ticket.tickets.find((t) => t.id === currentTicket.id)
      : null
  );

  // Fallback to local if not found (safety)
  const displayedTicket = realtimeCurrentTicket || currentTicket;
  const isTicketCompleted =
    displayedTicket?.status === "resolved" ||
    displayedTicket?.status === "closed";

  const ticketStatus = displayedTicket?.status;

  // status flags
  const isOpenLike = ticketStatus === "open" || ticketStatus === "reopened";
  const isInProgress = ticketStatus === "in_progress";
  const isEscalated = ticketStatus === "escalated";
  const isCompleted = ticketStatus === "resolved" || ticketStatus === "closed";

  // history flags
  const hasEverBeenInProgress = previousStatuses.includes("in progress");

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Raise Ticket"
        description="Create and manage support tickets"
        action={
          canCreate
            ? { label: "Raise Ticket", onClick: () => openForm() }
            : null
        }
      />

      <DataTable
        // key={`page-${page}-${pageSize}`}
        totalCount={pagination.count}
        data={tickets || []}
        columns={columns}
        searchPlaceholder="Search tickets..."
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        showExport={false}
        showImport={false}
        onDelete={async (id) => {
          await dispatch(deleteTicket(id));
          refetch();
        }}
        onRestore={async (id) => {
          await dispatch(restoreRole({ entityType: "tickets", id }));
          refetch();
        }}
      />

      {/* Create/Edit Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {currentTicket ? "Edit Ticket" : "Raise New Ticket"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* IT / Non-IT Toggle */}
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-muted/50 rounded-lg">
              <Label className="text-base font-medium">Ticket Type</Label>
              <div className="flex items-center gap-4">
                <span
                  className={
                    !isITRelated ? "font-medium" : "text-muted-foreground"
                  }
                >
                  Non-IT
                </span>
                <Switch
                  checked={isITRelated}
                  onCheckedChange={(checked) => {
                    setIsITRelated(checked);
                    // Clear Category and Subcategory when type changes
                    setFormData(prev => ({ ...prev, category: "", sub_category: "" }));
                  }}
                />
                <span
                  className={
                    isITRelated
                      ? "font-medium text-primary"
                      : "text-muted-foreground"
                  }
                >
                  IT Related
                </span>
              </div>
            </div>

            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter ticket title"
                className={formErrors.title ? "border-red-500" : ""}
              />
              {formErrors.title && (
                <p className="text-sm text-red-600 mt-1">{formErrors.title}</p>
              )}
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Describe your issue"
                className={`min-h-32 ${formErrors.description ? "border-red-500" : ""
                  }`}
              />
              {formErrors.description && (
                <p className="text-sm text-red-600 mt-1">
                  {formErrors.description}
                </p>
              )}
            </div>

            {/* Category Dropdown */}
            <FormField
              label="Category"
              name="category"
              type="select"
              value={formData.category}
              onChange={(e) => {
                setFormData({ ...formData, category: e.target.value, sub_category: "" });
              }}
              options={
                filteredCategories?.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })) || []
              }
              placeholder={
                categoriesLoading
                  ? "Loading categories..."
                  : "Select category"
              }
              disabled={categoriesLoading}
              // required
              error={formErrors.category}
            />

            <FormField
              label="Sub Category"
              name="sub_category"
              type="select"
              value={formData.sub_category}
              onChange={(e) =>
                setFormData({ ...formData, sub_category: e.target.value })
              }
              options={
                currentSubcategories?.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })) || []
              }
              placeholder={
                subcategoriesLoading
                  ? "Loading subcategories..."
                  : "Select subcategory"
              }
              disabled={subcategoriesLoading || !formData.category}
              required
              error={formErrors.sub_category}
            />

            <FormField
              label="Assign To"
              name="assigned_to"
              type="select"
              value={formData.assigned_to}
              onChange={(e) =>
                setFormData({ ...formData, assigned_to: e.target.value })
              }
              options={
                currentEmployees?.map((emp) => ({
                  value: emp.id,
                  label: emp.name, // or: `${emp.first_name} ${emp.last_name} (${emp.code})` if you want full details
                })) || []
              }
              placeholder={
                currentEmployees ? "Select employee" : "Loading employees..."
              }
              disabled={!currentEmployees} // disables if no data (loading or error)
              required
              error={formErrors.assigned_to}
            />

            <div>
              <Label>Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={(v) => setFormData({ ...formData, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {currentTicket ? "Update" : "Raise"} Ticket
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          {displayedTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>
                    {displayedTicket?.ticket_code} - {displayedTicket.title}
                    <Badge
                      variant={priorityColors[displayedTicket.priority]}
                      className="ml-2"
                    >
                      {displayedTicket.priority.toUpperCase()}
                    </Badge>
                  </span>
                </DialogTitle>

                <DialogDescription className="space-y-6">
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">
                      Description:
                    </span>
                    <p className="mt-2 text-base">
                      {displayedTicket.description}
                    </p>
                  </div>
                  {/* Always show current expected time if exists */}
                  {displayedTicket.status !== "closed" && (
                    <>
                      {displayedTicket.expected_solution_end_datetime && (
                        <Badge variant="outline" className="text-sm">
                          Current:{" "}
                          {formatDateTime(
                              displayedTicket.expected_solution_end_datetime
                            )
                           
                          }
                        </Badge>
                      )}

                      {/* Overdue warning */}
                      {displayedTicket.expected_solution_end_datetime &&
                        new Date(
                          displayedTicket.expected_solution_end_datetime
                        ) < new Date() && (
                          <p className="text-sm text-orange-600 flex items-center gap-1 mt-2">
                            <AlertCircle className="h-4 w-4" />
                            This ticket is past expected resolution time
                          </p>
                        )}
                    </>
                  )}

                  {/* Expected Resolution Time Editor - Show only when needed */}
                  {canApproveOrReject && canUpdate && (
                    <>
                      {/* Show editor if no time OR ticket was reopened */}
                      {displayedTicket.status !== "closed" &&
                        (!displayedTicket.expected_solution_end_datetime ||
                          displayedTicket.status === "reopened") && (
                          <div className="space-y-3 p-5 bg-muted/30 rounded-lg border">
                            <div className="flex items-center gap-2">
                              <Clock className="h-5 w-5 text-muted-foreground" />
                              <Label className="text-base font-medium">
                                {displayedTicket.expected_solution_end_datetime
                                  ? "Update"
                                  : "Set"}{" "}
                                Expected Resolution Time
                              </Label>
                            </div>

                            <div className="flex items-center gap-4 flex-wrap">
                            <Input
                              type="datetime-local"
                              min={new Date().toISOString().slice(0, 16)}
                              defaultValue={
                                displayedTicket.expected_solution_end_datetime
                                  ? displayedTicket.expected_solution_end_datetime.slice(0, 16)
                                  : ""
                              }
                              onChange={(e) => {
                                setCurrentTicket({
                                  ...displayedTicket,
                                  _tempResolutionTime: e.target.value || undefined,
                                });
                              }}
                              className={cn(
                                "max-w-xs",
                                // Optional: light red border only when value exists and invalid
                                currentTicket?._tempResolutionTime &&
                                  new Date(currentTicket._tempResolutionTime) <= new Date() &&
                                  "border-orange-400/70 focus-visible:ring-orange-400/30"
                              )}
                            />

                                                        <Button
                              size="sm"
                              onClick={async () => {
                                const newDateTime = currentTicket?._tempResolutionTime;

                                // ─── Validation happens HERE ───
                                if (!newDateTime) {
                                  toast.error("Please select a date & time");
                                  return;
                                }

                                const selectedTime = new Date(newDateTime);

                                const now = new Date();
                            const graceMs = 1 * 60 * 1000; // 1 minutes grace

                            if (selectedTime < new Date(now.getTime() - graceMs)) {
                              toast.error("Please select current date/time or a future date/time");
                              return;
                            }

                        // ─── If we reach here → time is valid ───
                        try {
                          await dispatch(
                            updateTicket({
                              id: displayedTicket.id,
                              expected_solution_end_datetime: selectedTime.toISOString(),
                            })
                          ).unwrap();

                          toast.success("Resolution time updated successfully");

                          const updatedDatetime = selectedTime.toISOString();

                          setCurrentTicket((prev) => ({
                            ...prev,
                            expected_solution_end_datetime: updatedDatetime,
                            _tempResolutionTime: undefined,
                          }));

                          dispatch(
                            setRealtimeTickets({
                              ...displayedTicket,
                              expected_solution_end_datetime: updatedDatetime,
                            })
                          );

                          await dispatch(fetchMessages(displayedTicket.id));
                        } catch (err) {
                          console.error("Update failed:", err);
                          toast.error("Failed to update resolution time");
                        }
                      }}
                      className="ml-auto"
                      disabled={isLoading || !currentTicket?._tempResolutionTime}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {displayedTicket.expected_solution_end_datetime ? "Update Time" : "Save Time"}
                    </Button>
                            </div>
                          </div>
                        )}
                    </>
                  )}

                  {/* Read-only for non-updaters */}
                  {!canUpdate &&
                    displayedTicket.expected_solution_end_datetime && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>Expected Resolution:</span>
                        <span className="font-medium">
                          {formatDateTime(
                              displayedTicket.expected_solution_end_datetime
                            )
                        }
                        </span>
                      </div>
                    )}
                </DialogDescription>
              </DialogHeader>

              <div className="flex gap-3 my-4 flex-wrap items-center">
                {(() => {
                  const ticket = displayedTicket;
                  const ticketStatus = displayedTicket?.status;
                  // const hasExpectedTime = !!ticket.expected_solution_end_datetime;
                  // const hasExpectedTime = !!displayedTicket?.expected_solution_end_datetime;

                  const hasExpectedTime =
                    !!displayedTicket?.expected_solution_end_datetime;

                  // const isInProgress = ticket.status === "in_progress";
                  const isResolved = ticket.status === "resolved";
                  const isClosed = ticket.status === "closed";
                  // const isCompleted = isResolved || isClosed;
                  const isReopened = ticket.status === "reopened";
                  const acknowledged = ticket.status === "ack_pending";
                  // const canSetInProgress =
                  //   !isCompleted &&
                  //   isOpenLike &&
                  //   !hasEverBeenInProgress &&
                  //   !isEscalated;

                  const isCompleted =
                    ticketStatus === "resolved" || ticketStatus === "closed";
                  const isInProgress = ticketStatus === "in_progress";

                  const canEditExpectedTime =
                    canApproveOrReject && canUpdate && !isCompleted;

                  // const canSetInProgress =
                  //   !isCompleted &&
                  //   !isInProgress &&
                  //   hasExpectedTime &&
                  //   ["open", "reopened", "escalated"].includes(ticketStatus);

                  const canSetInProgress =
                    !isCompleted &&
                    hasExpectedTime &&
                    (ticketStatus === "open" ||
                      ticketStatus === "reopened" ||
                      ticketStatus === "escalated") &&
                    !isInProgress;

                  // const canResolve =
                  //   !isCompleted &&
                  //   (
                  //     isInProgress ||
                  //     (isEscalated && hasEverBeenInProgress)
                  //   );

                  // const canResolve =
                  //   !isCompleted &&
                  //   hasExpectedTime &&
                  //   (isInProgress || ticketStatus === "escalated");

                  const canResolve =
                    !isCompleted && hasExpectedTime && isInProgress;

                  const disableOtherActionsWhileWaitingForInProgress =
                    hasExpectedTime &&
                    !isInProgress &&
                    (ticketStatus === "open" ||
                      ticketStatus === "reopened" ||
                      ticketStatus === "escalated");

                  // const wasInProgress = latestStatusChange?.oldStatus === "in_progress";

                  const handleAction = async (actionDispatch, successMsg) => {
                    try {
                      await actionDispatch.unwrap();
                      toast.success(successMsg);
                      await dispatch(fetchMessages(ticket.id));
                    } catch (err) {
                      toast.error(
                        err?.error || err?.detail || "Operation failed"
                      );
                    }
                  };

                  return (
                    <>
                      {/* Set In Progress - Only disabled if no time or already in progress */}
                      {!acknowledged && !isClosed && (
                        // <Button
                        //   size="sm"
                        //   variant={isInProgress ? "secondary" : "default"}
                        //   onClick={() => handleAction(dispatch(setInProgress(ticket.id)), "Ticket set to In Progress")}
                        //   // disabled={!hasExpectedTime || isInProgress || isLoading || wasInProgress}
                        //   disabled={!canSetInProgress || isLoading}
                        // >
                        //   {isInProgress ? "In Progress" : "Set In Progress"}
                        // </Button>}
                        <Button
                          size="sm"
                          variant={isInProgress ? "secondary" : "default"}
                          onClick={() =>
                            handleAction(
                              dispatch(setInProgress(displayedTicket.id)),
                              "Ticket set to In Progress"
                            )
                          }
                          disabled={!canSetInProgress || isLoading}
                        >
                          {isInProgress ? "In Progress" : "Set In Progress"}
                        </Button>
                      )}

                      {/* Resolve */}
                      {/* <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleAction(dispatch(resolveTicket(ticket.id)), "Ticket resolved")}
                        disabled={!isInProgress || isLoading}
                      >
                        Resolve
                      </Button> */}

                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => setResolveOpen(true)} // Open modal instead of direct API call
                        // disabled={!isInProgress || isLoading}
                        // disabled={isLoading || !isInProgress || !wasInProgress}
                        // disabled={isLoading || !isCurrentlyEscalated || !isInProgress}

                        //  disabled={ !wasInProgress ? (  isLoading ||
                        //     !isCurrentlyEscalated) :
                        //    (  isLoading ||
                        //     // !isCurrentlyEscalated
                        //     //  ||
                        //     // !isInProgress ||
                        //     !wasInProgress)
                        //   }

                        disabled={!canResolve || isLoading}
                      >
                        Resolve
                      </Button>

                      {/* Reopen */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleAction(
                            dispatch(reopenTicket(ticket.id)),
                            "Ticket reopened"
                          )
                        }
                        // disabled={!isCompleted || isLoading}
                        disabled={
                          !isCompleted ||
                          disableOtherActionsWhileWaitingForInProgress ||
                          isLoading
                        }
                      >
                        Reopen
                      </Button>

                      {/* Acknowledge Now */}
                      {isAllowed && ticket.status === "ack_pending" && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => {
                            dispatch(
                              setPendingAcknowledgement({
                                ticketId: ticket.id,
                                data: ticket,
                                open: true,
                              })
                            );
                          }}
                        >
                          Acknowledge Now
                        </Button>
                      )}

                      {/* Guidance */}
                      {!hasExpectedTime && ticketStatus !== "closed" && (
                        <div className="flex items-center gap-2 text-orange-600 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          <span>
                            Set Expected Resolution Time to enable "Set In
                            Progress"
                          </span>
                        </div>
                      )}

                      {isCompleted && (
                        <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                          <CheckCircle className="h-4 w-4" />
                          <span>Ticket Completed</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Messages & Input - unchanged */}
              <ScrollArea className="flex-1 pr-4 overflow-auto">
                {(messages[currentTicket.id] || []).map((msg) => (
                  <Card key={msg.id} className="mb-4">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {msg.sender_name[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{msg.sender_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDateTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                        {msg.is_me && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem
                                onClick={() => setEditingMsg(msg)}
                              >
                                <Edit2 className="mr-2 h-4 w-4" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  dispatch(
                                    removeMessage({
                                      ticketId: currentTicket.id,
                                      messageId: msg.id,
                                    })
                                  )
                                }
                                className="text-destructive"
                              >
                                <Trash className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {editingMsg?.id === msg.id ? (
                        <div className="flex gap-2">
                          <input
                            className="flex-1 px-3 py-2 border rounded-lg"
                            defaultValue={msg.message}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                dispatch(
                                  editMessage({
                                    ticketId: currentTicket.id,
                                    messageId: msg.id,
                                    message: e.target.value,
                                  })
                                ).then(() => setEditingMsg(null));
                              }
                            }}
                          />
                          <Button size="sm" onClick={() => setEditingMsg(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.message}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </ScrollArea>

              {/* <div className="flex gap-2 mt-4">
                <input
                  className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMsg()}
                />
                <Button onClick={sendMsg} disabled={isLoading}>
                  <Send className="h-4 w-4 mr-2" /> Send
                </Button>
              </div> */}
              {/* Chat Input - Disabled when ticket completed */}
              <div className="flex gap-2 mt-4">
                <input
                  className={cn(
                    "flex-1 px-4 py-2 border rounded-lg focus:outline-none transition-all",
                    isTicketCompleted
                      ? "bg-muted/50 text-muted-foreground cursor-not-allowed"
                      : "focus:ring-2 focus:ring-primary"
                  )}
                  placeholder={
                    isTicketCompleted
                      ? "Ticket is completed — messaging disabled"
                      : "Type a message..."
                  }
                  value={newMessage}
                  onChange={(e) =>
                    !isTicketCompleted && setNewMessage(e.target.value)
                  }
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey &&
                      !isTicketCompleted
                    ) {
                      sendMsg();
                    }
                  }}
                  disabled={isTicketCompleted}
                />
                <Button
                  onClick={sendMsg}
                  disabled={
                    isLoading || isTicketCompleted || !newMessage.trim()
                  }
                  className={cn(isTicketCompleted && "opacity-60")}
                >
                  <Send className="h-4 w-4 mr-2" /> Send
                </Button>
              </div>

              {/* Optional: Clear visual banner when completed */}
              {isTicketCompleted && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5" />
                    <div>
                      <p className="font-medium">Ticket Completed</p>
                      <p>
                        No further messages can be sent. The conversation is now
                        closed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* TICKET HISTORY DIALOG – USING GENERAL ACTIVITY LOG API */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl">Ticket History</DialogTitle>
            {/* <DialogDescription>
              Audit trail for ticket:{" "}
              <strong className="text-primary">
                #
                {(selectedTicketId &&
                  ticketLogs.find((l) => l.object_id === selectedTicketId)
                    ?.object_repr) ||
                  "Loading..."}
              </strong>
            </DialogDescription> */}
            <DialogDescription>
            Audit trail for ticket:{" "}
            <strong className="text-primary">
              #
              {(selectedTicketId &&
                ticketLogs.find((l) => l.object_id === selectedTicketId)
                  ?.object_model) || // <--- CHANGE THIS FROM object_repr
                "Loading..."}
            </strong>
          </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[65vh] pr-4">
            {logsLoading ? (
              <div className="text-center py-16 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
                <p>Loading history...</p>
              </div>
            ) : ticketLogs.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No activity recorded for this ticket</p>
              </div>
            ) : (
              <div className="space-y-6 py-4">
                {ticketLogs
                  .filter(
                    (log) =>
                      log.object_id === selectedTicketId &&
                      log.resource === "ticket" && 
                      !(
                        // log.action_type === "auto_escalate" || 
                        log.data?.changes?.escalation_cycle
                      )
                  )
                  .map((log) => {
                    const by = log.created_by
                      ? `${log.created_by.first_name || ""} ${log.created_by.last_name || ""
                        }`.trim() ||
                      log.created_by.code ||
                      "Unknown"
                      : "System";

                    return (
                      <Card
                        key={log.id}
                        className="p-6 hover:shadow-md transition-all border"
                      >
                        <div className="gap-4">
                          {/* Timeline */}
                          <div className="text-start text-xs text-muted-foreground shrink-0 mb-2">
                            <p className="font-medium">
                              {formatDateTime(log.created_at)}
                            </p>
                            {/* <p className="font-medium">
                              {formatDateTime(log.created_at)}
                            </p> */}
                          </div>

                          <div className="flex-1 space-y-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-semibold text-lg">
                                  {log.action_display || log.action_type}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  by <strong>{by}</strong>
                                </p>
                                {log.created_by?.code && (
                                  <p className="text-xs text-muted-foreground">
                                    ({log.created_by.code})
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge
                                  variant={
                                    log.action_type === "create"
                                      ? "success"
                                      : log.action_type === "update"
                                        ? "default"
                                        : log.action_type === "auto_escalate"
                                          ? "warning"
                                          : "destructive"
                                  }
                                >
                                  {log.action_type
                                    .replace("_", " ")
                                    .toUpperCase()}
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedLog(log);
                                    setDetailsOpen(true);
                                  }}
                                >
                                  <Eye className="h-4 w-4 mr-1" /> View Details
                                </Button>
                              </div>
                            </div>

                            {/* Summary */}
                            {/* {log.summary && (
                              <p className="text-foreground">{log.summary}</p>
                            )} */}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            )}
          </ScrollArea>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DETAILED LOG MODAL – USING YOUR LogDataDisplay COMPONENT */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl">Activity Details</DialogTitle>
            <DialogDescription>
              Full audit entry •{" "}
              {selectedLog &&
                formatDateTime(selectedLog.created_at)}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <ScrollArea className="max-h-[70vh] pr-4">
              <div className="space-y-8 py-6">
                {/* Header Summary */}
                <div className="bg-card border rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Performed by
                      </p>
                      <p className="text-xl font-bold">
                        {selectedLog.created_by
                          ? `${selectedLog.created_by.first_name || ""} ${selectedLog.created_by.last_name || ""
                            }`.trim() || selectedLog.created_by.code
                          : "System"}
                      </p>
                      {selectedLog.created_by?.code && (
                        <p className="text-sm text-muted-foreground">
                          ({selectedLog.created_by.code})
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-lg px-4 py-2">
                        {selectedLog.resource_display || selectedLog.resource}
                      </Badge>
                      <div className="mt-2">
                        <Badge
                          variant={
                            selectedLog.action_type === "create"
                              ? "success"
                              : selectedLog.action_type === "update"
                                ? "default"
                                : selectedLog.action_type === "auto_escalate"
                                  ? "warning"
                                  : "destructive"
                          }
                          className="text-lg px-4 py-2"
                        >
                          {selectedLog.action_display ||
                            selectedLog.action_type
                              .replace("_", " ")
                              .toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* <div className="pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground">Summary</p>
              <p className="text-lg mt-2 font-medium text-foreground">
                {selectedLog.summary || 'No summary available'}
              </p>
            </div> */}
                </div>

                {/* MAIN LOG DATA – USING YOUR REUSABLE LogDataDisplay */}
                <LogDataDisplay logData={selectedLog.data} />
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add this new dialog after the existing dialogs in RaiseTicketPage.jsx */}
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Ticket</DialogTitle>
            <DialogDescription>
              Please provide a remark for resolving this ticket.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="resolve-remark">Resolution Remark</Label>
              <Textarea
                id="resolve-remark"
                placeholder="Enter resolution details..."
                value={resolveRemark}
                onChange={(e) => setResolveRemark(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={async () => {
                try {
                  await dispatch(
                    resolveTicket({
                      id: displayedTicket.id,
                      resolved_remark: resolveRemark,
                    })
                  ).unwrap();
                  toast.success("Ticket resolved successfully");
                  setResolveOpen(false);
                  setResolveRemark("");
                  await dispatch(fetchMessages(displayedTicket.id));
                } catch (err) {
                  console.error("Resolve failed:", err);
                  toast.error(
                    err?.error || err?.detail || "Failed to resolve ticket"
                  );
                }
              }}
              disabled={!resolveRemark.trim() || isLoading}
            >
              Resolve Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledgement Modal - Auto appears when ack_pending */}
      {isAllowed && (
        <Dialog
          open={!!pendingAcknowledgement?.open}
          onOpenChange={(open) => {
            if (!open) {
              // Allow closing, but keep in state so user can reopen from detail
              //   dispatch(setPendingAcknowledgement({
              //     ...pendingAcknowledgement,
              //     open: false
              //   }
              // )
              dispatch(clearPendingAcknowledgement());
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-orange-500" />
                Ticket Requires Your Acknowledgement
              </DialogTitle>
              <DialogDescription>
                Ticket{" "}
                <strong>{pendingAcknowledgement?.ticket?.ticket_code}</strong>{" "}
                has been closed and needs your confirmation.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <p className="text-sm font-medium">
                  {pendingAcknowledgement?.ticket?.title}
                </p>
              </div>
              <div>
                <Label>Description</Label>
                <p className="text-sm text-muted-foreground">
                  {pendingAcknowledgement?.ticket?.description}
                </p>
              </div>
              <div>
                <Label>Your Response</Label>
                <Textarea
                  placeholder="Add solution notes (optional for reject, recommended for accept)"
                  value={ackNotes}
                  onChange={(e) => setAckNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await dispatch(
                      acknowledgeTicket({
                        id: pendingAcknowledgement.ticket.id,
                        is_accepted: false,
                        solution_notes: ackNotes || "Rejected by staff",
                      })
                    ).unwrap();
                    toast.success("Ticket rejected & reopened");
                    dispatch(clearPendingAcknowledgement());
                    setAckNotes("");
                  } catch (err) {
                    toast.error("Failed to reject ticket");
                  }
                }}
              >
                Reject & Reopen
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={async () => {
                  try {
                    await dispatch(
                      acknowledgeTicket({
                        id: pendingAcknowledgement.ticket.id,
                        is_accepted: true,
                        solution_notes: ackNotes || "Accepted and closed",
                      })
                    ).unwrap();
                    toast.success("Ticket acknowledged & closed");
                    dispatch(clearPendingAcknowledgement());
                    setAckNotes("");
                  } catch (err) {
                    toast.error("Failed to acknowledge ticket");
                  }
                }}
              >
                Accept & Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default RaiseTicketPage;
